/**
 * Project dialog with view/edit toggle.
 * Mirrors the TaskDialog pattern: read-only by default, pencil enters edit mode.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { toast } from "sonner";
import { DELETE_TASK, SET_SECTION_LOCATION_RESPONSIBLE, UPDATE_PROCUREMENT_PACKAGE_RESPONSIBLE_USER, UPDATE_SECTION_RESPONSIBLE_USER } from "@/graphql/mutations";
import type { Project, User } from "@/types";
import { ProjectStatus, ProjectType } from "@/types";
import { useProjectSpace } from "@/hooks/useProjectSpace";
import { useProjectDialogData } from "@/hooks/useProjectDialogData";
import { hasCapability, ProjectCapability } from "@/lib/projectCapabilities";
import {
  useInspectionSyncStatus,
  useSyncProjectInspections,
} from "@/hooks/useInspectionSync";
import {
  useSchedulingSyncStatus,
  useSyncScheduling,
  type SchedulingSyncResult,
} from "@/hooks/useSchedulingSync";
import type {
  PlotState,
  ProcurementPackageState,
  SchedulingSpace,
  SpaceState,
} from "@/components/projects/hooks/useProjectInitData";
import type { UserOption } from "@/components/projects/hooks/useProjectInitialisationData";
import { ProjectColumnEditor } from "./ProjectColumnEditor";
import { MiniInitDialog } from "./MiniInitDialog";
import { ResponsibilityMatrixTab } from "./ResponsibilityMatrixTab";
import gRefreshButton from "@/assets/g_refresh_button.png";
import sitedriveRefreshButton from "@/assets/sitedrive_refresh_button.png";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DialogTabs, DialogTabsList, DialogTabsTrigger, DialogTabsContent } from "@/components/ui/dialog-tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { SpaceHierarchyTab } from "@/components/plots/SpaceHierarchyTab";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  ProjectIcon,
  StatusIcon,
  CalendarIcon,
  DocumentIcon,
  TypeIcon,
  SpinnerIcon,
  EditIcon,
  AddIcon,
  DeleteIcon,
  PlotIcon,
} from "@/lib/icons";

const STATUS_LABELS: Record<string, string> = {
  TENDERING: "Tarjousvaihe",
  LOST: "Hävitty",
  PLANNING: "Suunnittelu",
  IN_PROGRESS: "Käynnissä",
  COMPLETED: "Luovutettu",
  WARRANTY: "Takuuvaihe",
  ARCHIVED: "Arkistoitu",
};

const TYPE_LABELS: Record<string, string> = {
  KVR: "KVR-hanke",
  KOKONAISURAKKA: "Kokonaisurakka",
  PROJEKTIJOHTO: "Projektijohto",
};

const ROLE_TYPE_LABELS: Record<string, string> = {
  TYOPAALLIKKO: "Työpäällikkö",
  VASTAAVA_TYONJOHTAJA: "Vastaava työnjohtaja",
  TYONJOHTAJA: "Työnjohtaja",
  TYOMAAINSINOORI: "Työmaainsinööri",
  TUOTANTOINSINOORI: "Tuotantoinsinööri",
};

// Order here is also the display order of the "Vastuuhenkilöt" section
// (primaryRoles is sorted by index in this array).
const PRIMARY_ROLE_TYPES = [
  "VASTAAVA_TYONJOHTAJA",
  "TYOPAALLIKKO",
  "TYOMAAINSINOORI",
  "TUOTANTOINSINOORI",
];
// "Muut roolit" (Työnjohtaja). A row here is either hand-assigned — added from
// this tab, editable and removable — or derived by the backend from the user's
// responsibilities, in which case it stays read-only. `isManuallyAssigned` on
// the row is what tells the two apart.
const OTHER_ROLE_TYPES = ["TYONJOHTAJA"];

function formatAgo(iso: string | null): string {
  if (!iso) return "Ei muutoksia Sitedrivessa";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Ei muutoksia Sitedrivessa";
  const diffMin = Math.round((Date.now() - then) / 60_000);
  if (diffMin < 1) return "Synkronoitu juuri äsken";
  if (diffMin < 60) return `Synkronoitu ${diffMin} min sitten`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `Synkronoitu ${diffH} h sitten`;
  const diffD = Math.round(diffH / 24);
  return `Synkronoitu ${diffD} pv sitten`;
}

// ── Task-change diff (PP and WS checkbox toggles in edit mode) ────────────────
//
// When the user toggles a task checkbox in the Hankintapaketit tab while in
// edit mode, the change is staged in `editingPlots` (a clone of
// dialogData.plots seeded on enter-edit-mode). `computeTaskChanges` produces
// a human-readable diff that drives both the save-button dirty state and
// the confirmation dialog that pops up before tallennus.

interface TaskChange {
  kind: "added" | "deleted";
  label: string;
  // The fields below are kept on the struct so the future "actually persist
  // the change" implementation can locate which PP / section / space the
  // change came from without re-walking the diff.
  plotIndex: number;
  ppIndex: number;
  wsIndex: number | null;
  spaceIndex: number | null;
  taskName: string;
}

const PP_TASK_FIELDS: Array<[keyof ProcurementPackageState, string]> = [
  ["includeSuunnitelmiin", "Suunnitelmiin perehtyminen"],
  ["includeAliurakka", "Aliurakkasopimukseen perehtyminen"],
  ["includeAloitusedellytykset", "Aloitusedellytykset"],
  ["includeTaloudellinenLoppuselvitys", "Taloudellinen loppuselvitys"],
];

const WS_SPACE_TASK_FIELDS: Array<[keyof SpaceState, string]> = [
  ["includeMestanVastaanotto", "Mestan vastaanotto"],
  ["includeMalliasennustarkastus", "Malliasennustarkastus"],
  ["includeOsakohteenTarkastus", "Osakohteen tarkastus"],
  ["includeTyovaiheenvastaanotto", "Työvaiheenvastaanotto"],
  ["includeLiitedokumentit", "Liitedokumentit"],
];

function ppLabel(pp: ProcurementPackageState): string {
  const code = pp.code || "—";
  return pp.name ? `${code} – ${pp.name}` : code;
}

function wsLabel(ws: { sectionCode: string; sectionName: string }): string {
  const code = ws.sectionCode || "—";
  return ws.sectionName ? `${code} – ${ws.sectionName}` : code;
}

const PLOT_SPACE_TYPES = new Set(["PLOT", "TONTTI"]);

/** Walk the scheduling-space parentId chain from a leaf and produce a
 *  "RAK 2 > Piha > Pohjakerros" string. The plot row is represented by the
 *  caller's plotName (the editingPlots entry's label) rather than by the
 *  scheduling space at the top of the chain — the columnView in
 *  WorkSectionSpaceTree.stripPlotSpaces uses the same convention, so the
 *  alert and the editor agree on what "the plot" is named. */
function buildLocationTreePath(
  leafSpaceId: string,
  plotName: string,
  spaceById: Map<string, SchedulingSpace>,
): string {
  const parts: string[] = [];
  const visited = new Set<string>();
  let currentId: string | null | undefined = leafSpaceId;
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const space = spaceById.get(currentId);
    if (!space) break;
    if (space.type && PLOT_SPACE_TYPES.has(space.type)) break;
    parts.unshift(space.name);
    currentId = space.parentId;
  }
  return [plotName, ...parts].filter(Boolean).join(" > ");
}

function computeTaskChanges(
  original: PlotState[],
  current: PlotState[],
  spaceById: Map<string, SchedulingSpace>,
): TaskChange[] {
  const out: TaskChange[] = [];
  for (let pi = 0; pi < current.length; pi++) {
    const cPlot = current[pi];
    const oPlot = original[pi];
    if (!cPlot || !oPlot) continue;

    const plotName = cPlot.name || "—";

    for (let ppi = 0; ppi < cPlot.procurementPackages.length; ppi++) {
      const cPp = cPlot.procurementPackages[ppi];
      const oPp = oPlot.procurementPackages[ppi];
      if (!cPp || !oPp) continue;
      const cPpLabel = ppLabel(cPp);

      for (const [field, taskName] of PP_TASK_FIELDS) {
        if (cPp[field] !== oPp[field]) {
          out.push({
            kind: cPp[field] ? "added" : "deleted",
            label: `${plotName} / ${cPpLabel}: ${taskName}`,
            plotIndex: pi,
            ppIndex: ppi,
            wsIndex: null,
            spaceIndex: null,
            taskName,
          });
        }
      }

      for (let wsi = 0; wsi < cPp.workSections.length; wsi++) {
        const cWs = cPp.workSections[wsi];
        const oWs = oPp.workSections[wsi];
        if (!cWs || !oWs) continue;
        const cWsLabel = wsLabel(cWs);

        for (let si = 0; si < cWs.spaces.length; si++) {
          const cSp = cWs.spaces[si];
          const oSp = oWs.spaces[si];
          if (!cSp || !oSp) continue;
          const locationPath = buildLocationTreePath(
            cSp.schedulingSpaceId,
            plotName,
            spaceById,
          );

          for (const [field, taskName] of WS_SPACE_TASK_FIELDS) {
            if (cSp[field] !== oSp[field]) {
              out.push({
                kind: cSp[field] ? "added" : "deleted",
                label: `${plotName} / ${cPpLabel} / ${cWsLabel} / ${locationPath}: ${taskName}`,
                plotIndex: pi,
                ppIndex: ppi,
                wsIndex: wsi,
                spaceIndex: si,
                taskName,
              });
            }
          }
        }
      }
    }
  }
  return out;
}


interface RoleFormData {
  id?: string;
  version?: number;
  roleType: string;
  userId: string;
  originalUserId?: string;
  isNew: boolean;
  isDeleted: boolean;
  // Työnjohtaja rows only: false means the row is derived from the user's
  // responsibilities and is owned by the backend, so it stays read-only.
  isManuallyAssigned?: boolean;
}

interface ProjectDetailDialogProps {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEditMode?: boolean;
  users?: User[];
  onPlotClick?: (plot: { id: string; name: string; projectId: string }) => void;
  onUpdateProject?: (input: {
    id: string;
    version: number;
    name: string;
    description?: string;
    status: ProjectStatus;
    projectType: ProjectType;
    startDate?: string;
    objectiveEndDate?: string;
  }) => Promise<unknown>;
  getProjectRoles?: (projectId: string) => Promise<
    Array<{
      id: string;
      version: number;
      roleType: string;
      userId: string;
      isManuallyAssigned: boolean;
    }>
  >;
  createProjectRole?: (input: {
    userId: string;
    projectId: string;
    roleType: string;
  }) => Promise<unknown>;
  deleteProjectRole?: (id: string) => Promise<unknown>;
  onSaved?: () => void;
}

export function ProjectDetailDialog({
  project,
  open,
  onOpenChange,
  initialEditMode = false,
  users = [],
  onPlotClick,
  onUpdateProject,
  getProjectRoles,
  createProjectRole,
  deleteProjectRole,
  onSaved,
}: ProjectDetailDialogProps) {
  const { projectSpace, loading } = useProjectSpace(project?.id || null);

  // Front-end authorization gate, read from the server-resolved capability list
  // rather than recomputed from access level + project roles.
  //
  // Edit mode exposes updateProject and the plot / section / procurement-package
  // editors (MANAGE_SITE_STRUCTURE), project-role management
  // (MANAGE_PROJECT_ROLES) and responsible-person edits
  // (MANAGE_RESPONSIBILITIES). Every project role that holds one holds all
  // three, so one gate covers the whole edit surface; users without it see a
  // read-only dialog. Split this gate per capability if the tiers ever diverge
  // again — `access_policy.py` is the authority, not this comment.
  const canEditProject = hasCapability(
    project,
    ProjectCapability.MANAGE_SITE_STRUCTURE,
  );
  const canEditResponsibilityMatrix = hasCapability(
    project,
    ProjectCapability.MANAGE_RESPONSIBILITY_MATRIX,
  );

  const [activeTab, setActiveTab] = useState("info");
  const [procurementEverActive, setProcurementEverActive] = useState(false);
  const [miniInitState, setMiniInitState] =
    useState<SchedulingSyncResult | null>(null);

  // Surface Sitedrive's "changes available" signal on the Synkronointi tab
  // label. Apollo dedupes by variables, so the inner SitedriveSyncRow shares
  // this query's cache — no double-fetch.
  const hasSchedulingLink = !!project?.externalSchedulingPlatformProjectId;
  const { changesDetectedAt: schedulingChangesDetectedAt } =
    useSchedulingSyncStatus(
      open && hasSchedulingLink ? (project?.id ?? null) : null,
    );
  const hasSchedulingChanges =
    hasSchedulingLink && !!schedulingChangesDetectedAt;

  useEffect(() => {
    if (!open) {
      setProcurementEverActive(false);
      setActiveTab("info");
      setMiniInitState(null);
    }
  }, [open]);

  const procurementGate = open && procurementEverActive;
  const dialogData = useProjectDialogData({
    projectId: procurementGate ? (project?.id ?? null) : null,
    projectSpace,
    spaceLoading: loading,
  });

  const [updatePpResponsibleUser] = useMutation(
    UPDATE_PROCUREMENT_PACKAGE_RESPONSIBLE_USER,
  );
  const [updateSectionResponsibleUser] = useMutation(
    UPDATE_SECTION_RESPONSIBLE_USER,
  );
  const [setSectionLocationResponsible] = useMutation(
    SET_SECTION_LOCATION_RESPONSIBLE,
  );
  const [deleteTaskMutation] = useMutation(DELETE_TASK);

  const [editMode, setEditMode] = useState(initialEditMode);
  const [editorFocusTrigger, setEditorFocusTrigger] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    status: ProjectStatus.PLANNING,
    projectType: ProjectType.KVR,
    startDate: "",
    objectiveEndDate: "",
  });
  const [roles, setRoles] = useState<RoleFormData[]>([]);
  const [editingPlots, setEditingPlots] = useState<PlotState[]>([]);

  // Load the project's roles into `roles`, seeding an empty row for each
  // primary role the project does not have yet so the Vastuuhenkilöt section
  // always shows all four. Called on open and again after a save, so ids and
  // versions of just-created rows are current within one edit session.
  const loadRoles = async (id: string) => {
    if (!getProjectRoles) {
      setRoles([]);
      return;
    }
    try {
      const fetched = await getProjectRoles(id);
      const fetchedRoles: RoleFormData[] = fetched.map((r) => ({
        id: r.id,
        version: r.version,
        roleType: r.roleType,
        userId: r.userId,
        originalUserId: r.userId,
        isNew: false,
        isDeleted: false,
        isManuallyAssigned: r.isManuallyAssigned,
      }));
      const seeded = [...fetchedRoles];
      for (const rt of PRIMARY_ROLE_TYPES) {
        if (!fetchedRoles.some((r) => r.roleType === rt)) {
          seeded.push({ roleType: rt, userId: "", isNew: true, isDeleted: false });
        }
      }
      setRoles(seeded);
    } catch {
      setRoles([]);
    }
  };

  // Reset form when dialog opens or project changes.
  // Deps are primitives only — `project` and `getProjectRoles` are new refs
  // every parent render, which would cause an infinite state loop.
  const projectId = project?.id;
  useEffect(() => {
    if (!open || !project) return;
    setEditMode(initialEditMode);
    if (initialEditMode) setProcurementEverActive(true);
    setError(null);
    setEditingPlots([]);
    setForm({
      name: project.name || "",
      description: project.description || "",
      status: project.status as ProjectStatus,
      projectType: project.projectType as ProjectType,
      startDate: project.startDate || "",
      objectiveEndDate: project.objectiveEndDate || "",
    });

    if (projectId) {
      void loadRoles(projectId);
    } else {
      setRoles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, initialEditMode]);

  // Seed editingPlots when entering edit mode. dialogData.plots is only a set of
  // projectSpace-derived shells (empty procurementPackages) until the PP queries
  // return, so gate on `basicLoaded` rather than `plots.length` — otherwise a
  // seed captured from the shells would stick (the length>0 guard below blocks
  // any re-seed once editingPlots is non-empty).
  useEffect(() => {
    if (
      editMode &&
      editingPlots.length === 0 &&
      dialogData.basicLoaded
    ) {
      setEditingPlots(dialogData.plots);
    }
  }, [editMode, editingPlots.length, dialogData.plots, dialogData.basicLoaded]);

  // Task add/delete changes derived by diffing editingPlots against the
  // backend-loaded baseline (dialogData.plots).  Each entry feeds the save
  // confirmation dialog and counts towards `dirty`.
  const taskChanges = useMemo<TaskChange[]>(() => {
    if (!editMode || editingPlots.length === 0) return [];
    const spaceById = new Map(
      dialogData.allSchedulingSpaces.map((s) => [s.id, s]),
    );
    return computeTaskChanges(dialogData.plots, editingPlots, spaceById);
  }, [editMode, editingPlots, dialogData.plots, dialogData.allSchedulingSpaces]);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const ppDirty =
    editMode &&
    editingPlots.some((plot, pi) =>
      plot.procurementPackages.some((pp, ppi) => {
        const meta = dialogData.ppMetaMatrix[pi]?.[ppi];
        if (meta == null) return false;
        if (pp.responsibleUserId !== meta.originalResponsibleUserId) return true;
        return pp.workSections.some((ws, wsi) => {
          const wsMeta = dialogData.wsMetaMatrix[pi]?.[ppi]?.[wsi];
          const inspectorDirty =
            wsMeta != null && ws.inspectorUserId !== wsMeta.originalInspectorUserId;
          const slDirty = Object.values(ws.spaceResponsibles).some(
            (r) => r.responsibleUserId !== r.originalResponsibleUserId,
          );
          return inspectorDirty || slDirty;
        });
      }),
    );
  const dirty = (() => {
    if (!project) return false;
    const rolesDirty = roles.some(
      (r) =>
        r.isDeleted ||
        (r.isNew && !!r.userId) ||
        (r.id && r.userId !== r.originalUserId),
    );
    return (
      form.name !== (project.name || "") ||
      form.description !== (project.description || "") ||
      form.status !== project.status ||
      form.projectType !== project.projectType ||
      form.startDate !== (project.startDate || "") ||
      form.objectiveEndDate !== (project.objectiveEndDate || "") ||
      rolesDirty ||
      ppDirty ||
      taskChanges.length > 0
    );
  })();

  const handleSaveClick = () => {
    if (taskChanges.length > 0) {
      setConfirmOpen(true);
    } else {
      void handleSave();
    }
  };

  const handleConfirmSave = () => {
    setConfirmOpen(false);
    void handleSave();
  };

  const handleSave = async () => {
    if (!project) return;
    setSaving(true);
    setError(null);
    try {
      if (onUpdateProject) {
        await onUpdateProject({
          id: project.id,
          version: project.version,
          name: form.name,
          description: form.description || undefined,
          status: form.status,
          projectType: form.projectType,
          startDate: form.startDate || undefined,
          objectiveEndDate: form.objectiveEndDate || undefined,
        });

        for (const r of roles) {
          if (r.isDeleted && r.id && deleteProjectRole) {
            await deleteProjectRole(r.id);
          } else if (r.isNew && r.userId && createProjectRole) {
            await createProjectRole({
              userId: r.userId,
              projectId: project.id,
              roleType: r.roleType,
            });
          } else if (r.id && r.userId !== r.originalUserId && r.userId) {
            if (deleteProjectRole) await deleteProjectRole(r.id);
            if (createProjectRole)
              await createProjectRole({
                userId: r.userId,
                projectId: project.id,
                roleType: r.roleType,
              });
          }
        }
      }

      for (let pi = 0; pi < editingPlots.length; pi++) {
        for (let ppi = 0; ppi < editingPlots[pi].procurementPackages.length; ppi++) {
          const pp = editingPlots[pi].procurementPackages[ppi];
          const meta = dialogData.ppMetaMatrix[pi]?.[ppi];
          if (meta && pp.responsibleUserId !== meta.originalResponsibleUserId) {
            await updatePpResponsibleUser({
              variables: {
                input: {
                  id: meta.id,
                  version: meta.version,
                  responsibleUserId: pp.responsibleUserId,
                },
              },
            });
          }
          for (let wsi = 0; wsi < pp.workSections.length; wsi++) {
            const ws = pp.workSections[wsi];
            const wsMeta = dialogData.wsMetaMatrix[pi]?.[ppi]?.[wsi];
            if (wsMeta && ws.inspectorUserId !== wsMeta.originalInspectorUserId) {
              await updateSectionResponsibleUser({
                variables: {
                  input: {
                    id: wsMeta.id,
                    version: wsMeta.version,
                    responsibleUserId: ws.inspectorUserId,
                  },
                },
              });
            }
            if (wsMeta) {
              for (const entry of Object.values(ws.spaceResponsibles)) {
                // Viewer entries always carry a real locationId; null only
                // occurs in init/mini-init, which persist server-side instead.
                if (!entry.locationId) continue;
                if (entry.responsibleUserId !== entry.originalResponsibleUserId) {
                  await setSectionLocationResponsible({
                    variables: {
                      input: {
                        sectionId: wsMeta.id,
                        locationId: entry.locationId,
                        responsibleUserId: entry.responsibleUserId,
                      },
                    },
                  });
                }
              }
            }
          }
        }
      }

      // Persist task deletions (existing tasks whose checkbox was unchecked).
      // Adds are intentionally NOT persisted here — creating one of these tasks
      // faithfully requires the initializer (template subtasks / Congrid
      // inspection + points), so "added" changes are surfaced as unavailable in
      // the confirm dialog and left as no-ops until the add-as-initializer
      // feature lands. Deleting a main task cascades to its subtasks server-side
      // (TaskService.delete).
      for (const change of deletedTaskChanges) {
        let taskId: string | undefined;
        if (change.wsIndex == null) {
          // PP-level task: resolve by (procurementPackageId, title).
          const ppId =
            dialogData.ppMetaMatrix[change.plotIndex]?.[change.ppIndex]?.id;
          if (ppId) {
            taskId = dialogData.taskIdByPpTitle.get(`${ppId}:${change.taskName}`);
          }
        } else if (change.spaceIndex != null) {
          // WS/space-level task: resolve by (sectionId, locationId, title).
          const sectionId =
            dialogData.wsMetaMatrix[change.plotIndex]?.[change.ppIndex]?.[
              change.wsIndex
            ]?.id;
          const space =
            editingPlots[change.plotIndex]?.procurementPackages[change.ppIndex]
              ?.workSections[change.wsIndex]?.spaces[change.spaceIndex];
          const locationId = space
            ? dialogData.spaceIdToLocationId.get(space.schedulingSpaceId)
            : undefined;
          if (sectionId && locationId) {
            taskId = dialogData.taskIdBySecLocTitle.get(
              `${sectionId}:${locationId}:${change.taskName}`,
            );
          }
        }

        if (!taskId) {
          throw new Error(`Tehtävää ei löytynyt poistettavaksi: ${change.label}`);
        }
        const res = await deleteTaskMutation({ variables: { id: taskId } });
        if (!res.data?.deleteTask?.success) {
          throw new Error(
            res.data?.deleteTask?.message ||
              `Tehtävän poisto epäonnistui: ${change.label}`,
          );
        }
      }

      // Refresh the dialog's PP/section/section-location data before leaving
      // edit mode. A newly-created section-location can't be auto-inserted into
      // the cached list, so without this the just-saved responsible would not
      // show in the read-only view (which renders `dialogData.plots`).
      await dialogData.refetch();

      // Roles too: a just-created row needs its real id, and a Työnjohtaja row
      // that was degraded rather than removed has to come back as read-only.
      await loadRoles(project.id);

      onSaved?.();
      setEditMode(false);
      setEditingPlots([]);
    } catch (e) {
      console.error("Save project failed", e);
      setError(
        e instanceof Error && e.message
          ? e.message
          : "Tallennus epäonnistui. Yritä uudelleen.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!project) return;
    setEditMode(false);
    setError(null);
    setEditingPlots([]);
    setForm({
      name: project.name || "",
      description: project.description || "",
      status: project.status as ProjectStatus,
      projectType: project.projectType as ProjectType,
      startDate: project.startDate || "",
      objectiveEndDate: project.objectiveEndDate || "",
    });
    setRoles((prev) =>
      prev
        .filter((r) => !r.isNew || PRIMARY_ROLE_TYPES.includes(r.roleType))
        .map((r) => ({
          ...r,
          isDeleted: false,
          userId: r.isNew ? "" : (r.originalUserId ?? r.userId),
        })),
    );
  };

  const primaryRoles = roles
    .filter((r) => !r.isDeleted && PRIMARY_ROLE_TYPES.includes(r.roleType))
    .sort(
      (a, b) =>
        PRIMARY_ROLE_TYPES.indexOf(a.roleType) -
        PRIMARY_ROLE_TYPES.indexOf(b.roleType),
    );
  const otherRoles = roles.filter(
    (r) => !r.isDeleted && OTHER_ROLE_TYPES.includes(r.roleType),
  );

  const formatUserName = (userId: string) => {
    const u = users.find((x) => x.id === userId);
    return u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email : "-";
  };

  const addedTaskChanges = taskChanges.filter((c) => c.kind === "added");
  const deletedTaskChanges = taskChanges.filter((c) => c.kind === "deleted");

  return (
    <>
    <Dialog open={open} onOpenChange={(next) => { if (!next && saving) return; onOpenChange(next); }}>
      <DialogContent className="sm:max-w-[1300px] h-[min(720px,90dvh)] overflow-hidden flex flex-col shadow-xl border-0 pb-0 [&>button]:h-8 [&>button]:w-8 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:text-white [&>button]:opacity-80 [&>button]:rounded-md [&>button:hover]:opacity-100 [&>button:hover]:bg-white/15">
        <DialogHeader className="bg-primary text-primary-foreground rounded-t-lg space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <ProjectIcon className="h-6 w-6 text-primary-foreground/80 flex-shrink-0" />
              <DialogTitle asChild>
                <h2 className="text-2xl font-semibold tracking-tight leading-tight text-primary-foreground truncate">
                  {project?.name || "Hanke"}
                </h2>
              </DialogTitle>
            </div>

          </div>

          {project && (
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-primary-foreground/85">
              <div className="flex items-center gap-2">
                <StatusIcon className="h-4 w-4 text-primary-foreground/60" />
                <Badge
                  className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0 border border-white/25 bg-white/15 text-white hover:bg-white/25"
                  variant="secondary"
                >
                  {STATUS_LABELS[project.status] || project.status}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <TypeIcon className="h-4 w-4 text-primary-foreground/60" />
                <span>
                  {TYPE_LABELS[project.projectType] || project.projectType}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-primary-foreground/60" />
                <span className="tabular-nums">
                  {project.startDate
                    ? new Date(project.startDate).toLocaleDateString("fi")
                    : "-"}
                  <span className="mx-1.5 text-primary-foreground/50">–</span>
                  {project.objectiveEndDate
                    ? new Date(project.objectiveEndDate).toLocaleDateString("fi")
                    : "-"}
                </span>
              </div>
            </div>
          )}

          <DialogDescription className="sr-only">
            {project?.name || "Hanke"}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="mx-6 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <DialogTabs
          value={activeTab}
          onValueChange={(v) => {
            setActiveTab(v);
            if (v === "tasks") setProcurementEverActive(true);
          }}
        >
          <DialogTabsList columns={6}>
            <DialogTabsTrigger value="info" className="py-2">Tiedot</DialogTabsTrigger>
            <DialogTabsTrigger value="tasks">Tehtävät</DialogTabsTrigger>
            <DialogTabsTrigger value="users">Käyttäjät</DialogTabsTrigger>
            <DialogTabsTrigger value="responsibility">Vastuumatriisi</DialogTabsTrigger>
            <DialogTabsTrigger value="space">Tilahierarkia</DialogTabsTrigger>
            <DialogTabsTrigger value="syncronization">
              <span className="flex items-center gap-2">
                Synkronointi
                {hasSchedulingChanges && (
                  <span className="relative flex h-2 w-2" aria-label="Muutoksia saatavilla">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                  </span>
                )}
              </span>
            </DialogTabsTrigger>
          </DialogTabsList>

          <DialogTabsContent value="info">
            <ScrollArea className="h-full">
              {project ? (
                <div className="space-y-8 py-6 px-6">
                  {/* Basic info — always shown; inputs in edit mode, plain text in view mode */}
                  {editMode ? (
                    <div className="grid grid-cols-3 gap-x-6 gap-y-5">
                      <InfoField
                        icon={<ProjectIcon className="h-4 w-4" />}
                        label="Nimi"
                      >
                        <Input
                          value={form.name}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, name: e.target.value }))
                          }
                          placeholder="Hankkeen nimi"
                        />
                      </InfoField>

                      <InfoField icon={<StatusIcon className="h-4 w-4" />} label="Tila">
                        <Select
                          value={form.status}
                          onValueChange={(v) =>
                            setForm((f) => ({ ...f, status: v as ProjectStatus }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(ProjectStatus).map((s) => (
                              <SelectItem key={s} value={s}>
                                {STATUS_LABELS[s] || s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </InfoField>

                      <InfoField
                        icon={<TypeIcon className="h-4 w-4" />}
                        label="Hanketyyppi"
                      >
                        <Select
                          value={form.projectType}
                          onValueChange={(v) =>
                            setForm((f) => ({
                              ...f,
                              projectType: v as ProjectType,
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(ProjectType).map((t) => (
                              <SelectItem key={t} value={t}>
                                {TYPE_LABELS[t] || t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </InfoField>

                      <InfoField
                        icon={<CalendarIcon className="h-4 w-4" />}
                        label="Aloituspäivä"
                      >
                        <Input
                          type="date"
                          value={form.startDate}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, startDate: e.target.value }))
                          }
                        />
                      </InfoField>

                      <InfoField
                        icon={<CalendarIcon className="h-4 w-4" />}
                        label="Tavoitevalmistuminen"
                      >
                        <Input
                          type="date"
                          value={form.objectiveEndDate}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              objectiveEndDate: e.target.value,
                            }))
                          }
                        />
                      </InfoField>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-x-6 gap-y-5">
                      <InfoField
                        icon={<ProjectIcon className="h-4 w-4" />}
                        label="Nimi"
                      >
                        <p className="text-sm">{project.name || "–"}</p>
                      </InfoField>

                      <InfoField icon={<StatusIcon className="h-4 w-4" />} label="Tila">
                        <p className="text-sm">
                          {STATUS_LABELS[project.status] || project.status}
                        </p>
                      </InfoField>

                      <InfoField
                        icon={<TypeIcon className="h-4 w-4" />}
                        label="Hanketyyppi"
                      >
                        <p className="text-sm">
                          {TYPE_LABELS[project.projectType] || project.projectType}
                        </p>
                      </InfoField>

                      <InfoField
                        icon={<CalendarIcon className="h-4 w-4" />}
                        label="Aloituspäivä"
                      >
                        <p className="text-sm">
                          {project.startDate
                            ? new Date(project.startDate).toLocaleDateString("fi")
                            : "–"}
                        </p>
                      </InfoField>

                      <InfoField
                        icon={<CalendarIcon className="h-4 w-4" />}
                        label="Tavoitevalmistuminen"
                      >
                        <p className="text-sm">
                          {project.objectiveEndDate
                            ? new Date(project.objectiveEndDate).toLocaleDateString("fi")
                            : "–"}
                        </p>
                      </InfoField>
                    </div>
                  )}

                  {/* Description */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-medium text-muted-foreground">
                      <DocumentIcon className="h-4 w-4" />
                      Kuvaus
                    </div>
                    {editMode ? (
                      <Textarea
                        value={form.description}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, description: e.target.value }))
                        }
                        placeholder="Hankkeen kuvaus"
                        rows={3}
                      />
                    ) : (
                      <p className="text-sm">
                        {project.description || (
                          <span className="italic text-muted-foreground">
                            Ei kuvausta
                          </span>
                        )}
                      </p>
                    )}
                  </div>

                  {/* Plots summary */}
                  {projectSpace && projectSpace.plots.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-medium text-muted-foreground">
                          <PlotIcon className="h-4 w-4" />
                          Tontit
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {projectSpace.plots.length}
                        </span>
                      </div>
                      <div className="rounded-md border bg-muted/20 divide-y max-h-[280px] overflow-y-auto">
                        {projectSpace.plots.map((plot) => (
                          <button
                            key={plot.id}
                            type="button"
                            onClick={() =>
                              onPlotClick?.({
                                id: plot.id,
                                name: plot.name,
                                projectId: project.id,
                              })
                            }
                            disabled={!onPlotClick}
                            className="group w-full flex items-baseline gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/40 disabled:cursor-default disabled:hover:bg-transparent"
                          >
                            <span className="flex-1 min-w-0 text-sm font-medium truncate">
                              {plot.name}
                            </span>
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {plot.locations.length} tilaa
                            </span>
                            <span className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-disabled:hidden">
                              →
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-8 px-6 text-center text-muted-foreground text-sm">
                  Hanketta ei löydetty
                </div>
              )}
            </ScrollArea>
          </DialogTabsContent>

          <DialogTabsContent value="tasks">
            {dialogData.loadingBasic ? (
              <div className="space-y-4 py-4 px-6">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
            ) : (
              <ProjectColumnEditor
                plots={editMode ? editingPlots : dialogData.plots}
                users={users as UserOption[]}
                allSchedulingSpaces={dialogData.allSchedulingSpaces}
                spaceIdToLocationId={dialogData.spaceIdToLocationId}
                templateTaskAvailability={dialogData.templateTaskAvailability}
                onUpdatePlot={(idx, updated) =>
                  setEditingPlots((prev) =>
                    prev.map((p, i) => (i === idx ? updated : p)),
                  )
                }
                readOnly={!editMode}
                taskCheckboxesReadOnly={!editMode}
                focusTrigger={editorFocusTrigger}
              />
            )}
          </DialogTabsContent>

          <DialogTabsContent value="users" scrollable>
            <div className="space-y-8 py-5 px-6">
              <RoleSection
                title="Vastuuhenkilöt"
                roles={primaryRoles}
                allRoles={roles}
                roleTypes={PRIMARY_ROLE_TYPES}
                editMode={editMode}
                fixedRoles={true}
                users={users}
                formatUserName={formatUserName}
                onUpdate={setRoles}
              />
              <RoleSection
                title="Muut roolit"
                roles={otherRoles}
                allRoles={roles}
                roleTypes={OTHER_ROLE_TYPES}
                editMode={editMode}
                users={users}
                formatUserName={formatUserName}
                onUpdate={setRoles}
                allowAdd={true}
                addLabel="Lisää työnjohtaja"
                derivedHint="tehtävävastuusta periytynyt"
              />
            </div>
          </DialogTabsContent>

          <DialogTabsContent value="responsibility" scrollable>
            {project && (
              <ResponsibilityMatrixTab
                projectId={project.id}
                projectSpace={projectSpace}
                users={users}
                canEdit={canEditResponsibilityMatrix}
              />
            )}
          </DialogTabsContent>

          <DialogTabsContent value="space">
            {loading && !projectSpace ? (
              <div className="space-y-4 py-4 px-6">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : projectSpace ? (
              <ScrollArea className="h-full">
                <div className="space-y-4 px-6 pb-4">
                  {projectSpace.plots.length === 0 &&
                  projectSpace.locations.length === 0 ? (
                    <div className="py-8 px-6 text-center text-muted-foreground text-sm">
                      Ei tiloja
                    </div>
                  ) : (
                    <>
                      {projectSpace.plots.map((plot) => (
                        <SpaceHierarchyTab
                          key={plot.id}
                          locations={plot.locations}
                          plotLabel={plot.name}
                          hideEmptyState={true}
                        />
                      ))}
                      {(() => {
                        const plotLocationIds = new Set(
                          projectSpace.plots.flatMap((p) =>
                            p.locations.map((l) => l.id),
                          ),
                        );
                        const orphanLocations =
                          projectSpace.locations.filter(
                            (l) => !plotLocationIds.has(l.id),
                          );
                        if (orphanLocations.length > 0) {
                          return (
                            <SpaceHierarchyTab
                              locations={orphanLocations}
                              plotLabel="Hanketason tilat"
                              hideEmptyState={true}
                            />
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="py-8 px-6 text-center text-muted-foreground text-sm">
                Hanketta ei löydetty
              </div>
            )}
          </DialogTabsContent>

          <DialogTabsContent value="syncronization" scrollable>
            {project && (
              <div className="space-y-4 py-5 px-6">
                <CongridSyncRow
                  congridProjectId={
                    project.effectiveCongridProjectId ??
                    project.externalQualityInspectionPlatformProjectId ??
                    null
                  }
                />
                <SitedriveSyncRow
                  projectId={project.id}
                  schedulingProjectId={
                    project.externalSchedulingPlatformProjectId ?? null
                  }
                  onNewSectionsDetected={setMiniInitState}
                />

                {/* MiniInitDialog wired inside the tab. Today it still renders
                    as a modal (its internal <Dialog>); a later pass will rework
                    it into inline tab content. Keeping the JSX inside the tab
                    now means ownership and refetch wiring already live where
                    the rework will. */}
                {miniInitState && (
                  <MiniInitDialog
                    open={!!miniInitState}
                    onOpenChange={(next) => {
                      if (!next) {
                        onSaved?.();
                        setMiniInitState(null);
                      }
                    }}
                    projectId={project.id}
                    projectName={project.name}
                    schedulingProjectId={
                      project.externalSchedulingPlatformProjectId ?? null
                    }
                    syncResult={miniInitState}
                  />
                )}
              </div>
            )}
          </DialogTabsContent>
        </DialogTabs>

        {editMode && (
          <div className="px-6 py-4 border-t flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={saving}
            >
              Peruuta
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSaveClick}
              disabled={saving || !dirty || !form.name.trim()}
            >
              {saving ? (
                <>
                  <SpinnerIcon className="h-4 w-4 mr-1.5 animate-spin" />
                  Tallennetaan...
                </>
              ) : (
                "Tallenna"
              )}
            </Button>
          </div>
        )}
        {project && !editMode && canEditProject && (
          <div className="absolute right-14 top-4 z-50">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setProcurementEverActive(true);
                // Seed synchronously ONLY when the PP data has actually loaded
                // (warm cache). When cold — e.g. entering edit from the Tiedot
                // tab before Tehtävät was ever opened — dialogData.plots holds
                // only projectSpace shells with empty procurementPackages, so we
                // skip the seed and let the useEffect above seed once
                // basicLoaded flips true. Seeding shells here would stick and
                // leave the tasks tab showing no procurement packages.
                if (dialogData.basicLoaded) {
                  setEditingPlots(dialogData.plots);
                }
                setEditMode(true);
                setEditorFocusTrigger((v) => v + 1);
              }}
              title="Muokkaa"
              className="h-8 gap-2 p-2 text-white opacity-80 hover:opacity-100 hover:bg-white/15 rounded-md"
            >
              <EditIcon className="h-4 w-4" />
              Muokkaa
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <AlertDialog
      open={confirmOpen}
      onOpenChange={(next) => {
        if (saving) return;
        setConfirmOpen(next);
      }}
    >
      <AlertDialogContent className="max-w-[640px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Vahvista tehtävien muutokset</AlertDialogTitle>
          <AlertDialogDescription>
            Tallennus poistaa alla luetellut tehtävät. Tehtävien lisääminen ei ole vielä käytössä.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="max-h-[50vh] overflow-y-auto space-y-4 text-sm">
          {addedTaskChanges.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-dashed border-muted-foreground/30 p-3 opacity-70">
              <div className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
                Lisääminen ei ole vielä käytössä ({addedTaskChanges.length})
              </div>
              <p className="text-xs text-muted-foreground">
                Nämä valinnat jätetään huomiotta eikä tehtäviä luoda.
              </p>
              <ul className="space-y-1 list-disc pl-5 text-muted-foreground">
                {addedTaskChanges.map((c, i) => (
                  <li key={`add-${i}`}>{c.label}</li>
                ))}
              </ul>
            </div>
          )}
          {deletedTaskChanges.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
                Poistetaan ({deletedTaskChanges.length})
              </div>
              <ul className="space-y-1 list-disc pl-5">
                {deletedTaskChanges.map((c, i) => (
                  <li key={`del-${i}`}>{c.label}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Peruuta</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmSave} disabled={saving}>
            Vahvista ja tallenna
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function InfoField({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-medium text-muted-foreground">
        {icon}
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function RoleSection({
  title,
  roles,
  allRoles,
  roleTypes,
  editMode,
  fixedRoles = false,
  users,
  formatUserName,
  onUpdate,
  allowAdd = false,
  addLabel = "Lisää rooli",
  derivedHint,
}: {
  title: string;
  roles: RoleFormData[];
  allRoles: RoleFormData[];
  roleTypes: string[];
  editMode: boolean;
  fixedRoles?: boolean;
  users: User[];
  formatUserName: (userId: string) => string;
  onUpdate: React.Dispatch<React.SetStateAction<RoleFormData[]>>;
  allowAdd?: boolean;
  addLabel?: string;
  // When set, rows that are not hand-assigned stay read-only in edit mode and
  // carry this hint — they are backend-owned and cannot be edited or removed.
  derivedHint?: string;
}) {
  // A single-role-type section (Muut roolit) has nothing to choose in a role
  // dropdown, so it renders a fixed label instead — but unlike `fixedRoles` its
  // rows can be added and removed.
  const singleRoleType = roleTypes.length === 1;
  const rowEditable = (role: RoleFormData) =>
    !derivedHint || role.isNew || !!role.isManuallyAssigned;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 min-h-[28px]">
        <div className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
          {title}
        </div>
        {editMode && allowAdd && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              onUpdate((prev) => [
                ...prev,
                {
                  roleType: roleTypes[0],
                  userId: "",
                  isNew: true,
                  isDeleted: false,
                  isManuallyAssigned: true,
                },
              ])
            }
            className="h-7 text-xs"
          >
            <AddIcon className="h-3.5 w-3.5 mr-1" />
            {addLabel}
          </Button>
        )}
      </div>

      {roles.length === 0 ? (
        <p className="text-xs text-muted-foreground/60 italic">
          {editMode ? "Ei rooleja" : "-"}
        </p>
      ) : (
        <div className="space-y-2">
          {roles.map((role) => {
            const idx = allRoles.indexOf(role);
            return editMode && rowEditable(role) ? (
              fixedRoles ? (
                <div
                  key={role.id || `edit-${idx}`}
                  className="grid grid-cols-[11rem_1fr] items-center gap-2"
                >
                  <span className="text-sm text-muted-foreground">
                    {ROLE_TYPE_LABELS[role.roleType] || role.roleType}
                  </span>
                  <Combobox
                    options={users.map<ComboboxOption>((u) => ({
                      value: u.id,
                      label:
                        `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ||
                        u.email,
                      description: u.email,
                    }))}
                    value={role.userId || null}
                    onChange={(v) =>
                      onUpdate((prev) =>
                        prev.map((r, i) =>
                          i === idx ? { ...r, userId: v ?? "" } : r,
                        ),
                      )
                    }
                    placeholder="Valitse käyttäjä"
                    searchPlaceholder="Hae käyttäjää..."
                  />
                </div>
              ) : (
              <div
                key={role.id || `edit-${idx}`}
                className="grid grid-cols-[11rem_1fr_2rem] items-center gap-2"
              >
                {singleRoleType ? (
                  <span className="text-sm text-muted-foreground">
                    {ROLE_TYPE_LABELS[role.roleType] || role.roleType}
                  </span>
                ) : (
                  <Select
                    value={role.roleType}
                    onValueChange={(v) =>
                      onUpdate((prev) =>
                        prev.map((r, i) =>
                          i === idx ? { ...r, roleType: v } : r,
                        ),
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roleTypes.map((rt) => (
                        <SelectItem key={rt} value={rt}>
                          {ROLE_TYPE_LABELS[rt] || rt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Combobox
                  options={users.map<ComboboxOption>((u) => ({
                    value: u.id,
                    label:
                      `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ||
                      u.email,
                    description: u.email,
                  }))}
                  value={role.userId || null}
                  onChange={(v) =>
                    onUpdate((prev) =>
                      prev.map((r, i) =>
                        i === idx ? { ...r, userId: v ?? "" } : r,
                      ),
                    )
                  }
                  placeholder="Valitse käyttäjä"
                  searchPlaceholder="Hae käyttäjää..."
                />

                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() =>
                    onUpdate((prev) =>
                      role.isNew
                        ? prev.filter((_, i) => i !== idx)
                        : prev.map((r, i) =>
                            i === idx ? { ...r, isDeleted: true } : r,
                          ),
                    )
                  }
                  title="Poista"
                  className="h-8 w-8"
                >
                  <DeleteIcon className="h-4 w-4 text-destructive" />
                </Button>
              </div>
              )
            ) : (
              <div
                key={role.id || `view-${idx}`}
                className="grid grid-cols-[11rem_1fr] items-baseline gap-3"
              >
                <span className="text-xs text-muted-foreground">
                  {ROLE_TYPE_LABELS[role.roleType] || role.roleType}
                </span>
                <span className="text-sm">
                  {formatUserName(role.userId)}
                  {editMode && derivedHint && (
                    <span className="ml-2 text-xs text-muted-foreground/70">
                      {derivedHint}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CongridSyncRow({
  congridProjectId,
}: {
  congridProjectId: string | null;
}) {
  const hasLink = !!congridProjectId;
  const { lastSyncedAt, refetch } = useInspectionSyncStatus(
    congridProjectId || null,
  );
  const { loading, syncProject } = useSyncProjectInspections();

  const handleClick = async () => {
    if (!congridProjectId) return;
    try {
      await syncProject(congridProjectId);
      await refetch();
    } catch (err) {
      console.error("Manual inspection sync failed:", err);
    }
  };

  return (
    <div className="rounded-md border bg-muted/20 px-4 py-3 flex items-center gap-4">
      <img
        src={gRefreshButton}
        alt=""
        className={`h-6 w-6 flex-shrink-0 ${loading ? "animate-spin" : ""}`}
      />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-semibold tracking-tight">Congrid</span>
        <span className="text-xs text-muted-foreground truncate">
          {hasLink ? formatAgo(lastSyncedAt) : "Ei yhdistetty"}
        </span>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={loading || !hasLink}
        title={
          hasLink
            ? "Synkronoi Congridista"
            : "Hanketta ei ole yhdistetty Congridiin"
        }
      >
        {loading ? (
          <SpinnerIcon className="h-4 w-4 mr-1.5 animate-spin" />
        ) : null}
        Synkronoi
      </Button>
    </div>
  );
}

function SitedriveSyncRow({
  projectId,
  schedulingProjectId,
  onNewSectionsDetected,
}: {
  projectId: string;
  schedulingProjectId: string | null;
  onNewSectionsDetected: (result: SchedulingSyncResult) => void;
}) {
  const hasLink = !!schedulingProjectId;
  const { lastSyncedAt, changesDetectedAt, refetch: refetchSyncStatus } =
    useSchedulingSyncStatus(hasLink ? projectId : null);
  const { loading, syncScheduling } = useSyncScheduling();

  const handleClick = async () => {
    if (!hasLink) return;
    try {
      const result = await syncScheduling(projectId);
      if (result) {
        if (result.status === "new_sections_needed") {
          onNewSectionsDetected(result);
        } else if (result.status === "updated") {
          const msg = result.message || "Synkronoitu onnistuneesti";
          const stats = [
            result.sectionsUpdated && `${result.sectionsUpdated} osakohdetta`,
            result.sectionLocationsUpdated &&
              `${result.sectionLocationsUpdated} sijaintia`,
            result.ppsUpdated && `${result.ppsUpdated} hankintapakettia`,
            result.tasksUpdated && `${result.tasksUpdated} tehtävää`,
          ]
            .filter(Boolean)
            .join(", ");
          toast.success("Sitedrive-synkronointi", {
            description: stats ? `${msg} (${stats})` : msg,
          });
        }
        await refetchSyncStatus();
      }
    } catch (err) {
      console.error("Manual scheduling sync failed:", err);
      toast.error("Synkronointi epäonnistui", {
        description: "Sitedrive-synkronointi epäonnistui. Yritä uudelleen.",
      });
    }
  };

  const hasChanges = !!changesDetectedAt;
  const statusLabel = hasLink
    ? hasChanges
      ? "Muutoksia saatavilla"
      : formatAgo(lastSyncedAt) || "Ei vielä synkronoitu"
    : "Ei yhdistetty";

  return (
    <div className="rounded-md border bg-muted/20 px-4 py-3 flex items-center gap-4">
      <img
        src={sitedriveRefreshButton}
        alt=""
        className={`h-6 w-6 flex-shrink-0 ${loading ? "animate-spin" : ""}`}
      />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-semibold tracking-tight">Sitedrive</span>
        <div className="flex items-center gap-2 min-w-0">
          {hasChanges && hasLink && (
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>
          )}
          <span className="text-xs text-muted-foreground truncate">
            {statusLabel}
          </span>
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={loading || !hasLink}
        title={
          hasLink
            ? "Synkronoi Sitedrivesta"
            : "Hanketta ei ole yhdistetty Sitedriveen"
        }
      >
        {loading ? (
          <SpinnerIcon className="h-4 w-4 mr-1.5 animate-spin" />
        ) : null}
        Synkronoi
      </Button>
    </div>
  );
}
